export interface BlogPost {
  slug: string;
  title: string;
  date: string;
  excerpt: string;
  metaTitle: string;
  metaDescription: string;
  language: 'en' | 'de';
  content?: React.ComponentType | (() => JSX.Element);
  linkedinUrl?: string;
  isLinkedInPost?: boolean;
  image?: string;
  lastUpdated?: string;
  author?: string;
  tags?: string[];
}

// This will be populated by the MDX files
export const blogPosts: Record<string, BlogPost> = {};

// Cache for memoized sorted posts to improve performance
interface PostCache {
  all: BlogPost[] | null;
  en: BlogPost[] | null;
  de: BlogPost[] | null;
}

const cache: PostCache = {
  all: null,
  en: null,
  de: null
};

// Helper to register blog posts from MDX files
export const registerBlogPost = (post: BlogPost) => {
  blogPosts[post.slug] = post;
  // Invalidate cache when a new post is registered
  cache.all = null;
  cache.en = null;
  cache.de = null;
};

// Get all posts sorted by date (newest first)
// Optimization: Uses string comparison for dates and memoization to avoid re-sorting on every render
export const getAllPosts = (language?: 'en' | 'de'): BlogPost[] => {
  if (language) {
    if (!cache[language]) {
      // If language-specific cache is empty, derive from all posts (which are already sorted)
      cache[language] = getAllPosts().filter(post => post.language === language);
    }
    return cache[language]!;
  }

  if (!cache.all) {
    // Sort using string comparison (ISO 8601 dates sort correctly as strings)
    // This avoids expensive Date parsing on every render
    cache.all = Object.values(blogPosts).sort((a, b) => b.date.localeCompare(a.date));
  }
  return cache.all;
};

// Get a single post by slug
export const getPostBySlug = (slug: string): BlogPost | undefined => {
  return blogPosts[slug];
};

/**
 * Resets the blog posts and cache for testing purposes.
 * This clears all registered posts and invalidates all caches.
 */
export const resetPosts = () => {
  Object.keys(blogPosts).forEach(key => delete blogPosts[key]);
  cache.all = null;
  cache.en = null;
  cache.de = null;
};
